/**
 * MAUUsage.tsx (v2)
 *
 * Shared component for the "Usage" tab. Renders monthly active user count
 * vs. limit, a 12-month historical bar chart, status badge, and a
 * tier-branched CTA. Works in both contexts:
 *   - Management page: pass fetchUsage that hits /api/workspaces/:id/subscription/mau-usage
 *   - Widget:           pass fetchUsage that POSTs /api/client/subscription/mau-usage
 *
 * When `theme` prop is provided (widget context), uses theme.colors.* for styling
 * AND switches CTA copy to widget-side path strings. When omitted (management
 * context), falls back to v2 CSS variables and management-side path strings.
 *
 * No PII. Component only ever sees aggregate counts + the workspace's own
 * subscription metadata from props.
 *
 * v2 changes (2026-05-18):
 *   - useEffect now runs on mount only — was re-firing on every parent render
 *     because fetchUsage prop was a fresh arrow function each time.
 *   - CTACopy: new add-on-path messaging, widget vs mgmt branching.
 */

import React, { useEffect, useState } from 'react';
import {
  MAUUsageResponse,
  MAUUsageCurrent,
  MAUUsageHistoricalRow,
  MAUHealthStatus,
} from '../../widget/types';

const MONTH_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface MAUUsageProps {
  fetchUsage: () => Promise<MAUUsageResponse>;
  isLifetime: boolean;
  subscriptionStatus: string;
  /** Optional WidgetTheme; when omitted, component uses CSS vars + mgmt copy. */
  theme?: any;
}

type SlotRow =
  | MAUUsageHistoricalRow
  | { billing_year: number; billing_month: number; placeholder: true };

function pickColors(theme?: any) {
  if (theme && theme.colors) {
    return {
      text:          theme.colors.text,
      textMuted:     theme.colors.textMuted,
      textTertiary:  theme.colors.textTertiary,
      primary:       theme.colors.primary,
      success:       theme.colors.success,
      successBg:     theme.colors.successBg,
      warning:       theme.colors.warning,
      warningBg:     '#fef3c7',
      error:         theme.colors.error,
      errorBg:       theme.colors.errorBg,
      border:        theme.colors.border,
      bg:            theme.colors.bg,
      bgAlt:         theme.colors.bgAlt,
    };
  }
  return {
    text:          'var(--v2-text)',
    textMuted:     'var(--v2-text-muted)',
    textTertiary:  'var(--v2-text-tertiary)',
    primary:       'var(--v2-primary)',
    success:       'var(--v2-success-dark)',
    successBg:     'var(--v2-success-bg)',
    warning:       'var(--v2-warning-dark)',
    warningBg:     '#fef3c7',
    error:         'var(--v2-error)',
    errorBg:       'var(--v2-error-bg)',
    border:        'var(--v2-border)',
    bg:            'var(--v2-bg)',
    bgAlt:         'var(--v2-surface-alt)',
  };
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'recently';
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1)   return 'just now';
  if (minutes < 60)  return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function statusLabel(s: MAUHealthStatus | null): string {
  switch (s) {
    case 'healthy':     return 'Healthy';
    case 'approaching': return 'Approaching limit';
    case 'exceeded':    return 'Over limit';
    case 'critical':    return 'Critical';
    case 'persistent':  return 'Persistent overage';
    default:            return 'No data';
  }
}

function statusPalette(s: MAUHealthStatus | null, c: ReturnType<typeof pickColors>) {
  switch (s) {
    case 'healthy':     return { bg: c.successBg, fg: c.success };
    case 'approaching': return { bg: c.warningBg, fg: c.warning };
    case 'exceeded':    return { bg: c.errorBg,   fg: c.error };
    case 'critical':    return { bg: c.errorBg,   fg: c.error };
    case 'persistent':  return { bg: c.errorBg,   fg: c.error };
    default:            return { bg: c.bgAlt,     fg: c.textMuted };
  }
}

function buildSlots(current: MAUUsageCurrent | null, rows: MAUUsageHistoricalRow[]): SlotRow[] {
  let year: number;
  let month: number;
  if (current) {
    year = current.billing_year;
    month = current.billing_month;
  } else {
    const now = new Date();
    year = now.getUTCFullYear();
    month = now.getUTCMonth() + 1;
  }

  const rowMap = new Map<string, MAUUsageHistoricalRow>();
  for (const r of rows) rowMap.set(`${r.billing_year}-${r.billing_month}`, r);

  const slots: SlotRow[] = [];
  for (let i = 0; i < 12; i++) {
    let y = year;
    let m = month - i;
    while (m <= 0) { m += 12; y -= 1; }
    const key = `${y}-${m}`;
    const found = rowMap.get(key);
    slots.push(found || { billing_year: y, billing_month: m, placeholder: true });
  }
  slots.reverse(); // oldest first for display
  return slots;
}

const MAUUsage: React.FC<MAUUsageProps> = ({ fetchUsage, isLifetime, subscriptionStatus, theme }) => {
  const [data, setData] = useState<MAUUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const colors = pickColors(theme);
  const isWidget = !!theme;

  // Fire once on mount. The fetchUsage prop is recreated every parent render
  // (e.g. `() => api.getMauUsage()`), so depending on it would cause an
  // infinite refetch loop. We intentionally pin to mount; the tab unmounts
  // when you leave it, so re-clicking the tab gives a fresh fetch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchUsage()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Failed to load MAU usage'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>Loading usage…</div>;
  }
  if (error) {
    return (
      <div style={{ padding: '16px 20px', backgroundColor: colors.errorBg, color: colors.error, borderRadius: '6px' }}>
        {error}
      </div>
    );
  }

  const current = data?.current || null;
  const historical = data?.historical || [];
  const slots = buildSlots(current, historical);
  const maxValue = Math.max(
    1,
    ...slots.map(s => ('placeholder' in s ? 0 : Math.max(s.mau_count || 0, s.mau_limit_effective || 0)))
  );

  const status: MAUHealthStatus | null = current?.health_status || (current ? 'healthy' : null);
  const palette = statusPalette(status, colors);

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Header: big number + days remaining + status pill */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '20px',
      }}>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, lineHeight: 1.1 }}>
            {current?.mau_count ?? 0}
            <span style={{ fontSize: '1rem', fontWeight: 400, color: colors.textTertiary, marginLeft: '8px' }}>
              {current?.mau_limit_effective != null
                ? `of ${current.mau_limit_effective} active users`
                : 'active users'}
            </span>
          </div>
          <div style={{ fontSize: '0.875rem', color: colors.textTertiary, marginTop: '6px' }}>
            {current
              ? `${current.days_remaining_in_period} day${current.days_remaining_in_period === 1 ? '' : 's'} remaining in ${MONTH_SHORT[current.billing_month]} ${current.billing_year}`
              : 'No data yet for this period'}
          </div>
        </div>
        <div style={{
          padding: '6px 12px',
          backgroundColor: palette.bg,
          color: palette.fg,
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: 600,
          alignSelf: 'flex-start',
        }}>
          {statusLabel(status)}
        </div>
      </div>

      {/* Progress bar */}
      {current && current.mau_limit_effective != null && current.mau_limit_effective > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            height: '10px',
            backgroundColor: colors.bgAlt,
            borderRadius: '5px',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${Math.min(100, current.usage_pct || 0)}%`,
              height: '100%',
              backgroundColor: palette.fg,
              transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '6px',
            fontSize: '0.75rem',
            color: colors.textMuted,
          }}>
            <span>{current.usage_pct ?? 0}% used</span>
            <span>limit {current.mau_limit_effective}</span>
          </div>
        </div>
      )}

      {/* Historical chart — 12 div-bars, oldest left, newest right */}
      <div style={{ marginTop: '32px' }}>
        <div style={{
          fontSize: '0.6875rem',
          fontWeight: 500,
          color: colors.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '12px',
        }}>
          Last 12 Months
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '6px',
          height: '120px',
          padding: '0 2px',
        }}>
          {slots.map((s, idx) => {
            const isPlaceholder = 'placeholder' in s;
            const count = isPlaceholder ? 0 : (s.mau_count || 0);
            const limit = isPlaceholder ? null : s.mau_limit_effective;
            const overLimit = limit != null && limit > 0 && count > limit;
            const heightPct = (count / maxValue) * 100;
            const monthLabel = MONTH_SHORT[s.billing_month];
            const title = isPlaceholder
              ? `${monthLabel} ${s.billing_year}: no data`
              : `${monthLabel} ${s.billing_year}: ${count} MAU${limit != null ? ` of ${limit}` : ''}`;
            return (
              <div
                key={`${s.billing_year}-${s.billing_month}-${idx}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                  }}
                  title={title}
                >
                  {!isPlaceholder && (
                    <div
                      style={{
                        width: '100%',
                        height: `${Math.max(2, heightPct)}%`,
                        backgroundColor: overLimit ? colors.error : colors.primary,
                        borderRadius: '3px 3px 0 0',
                        opacity: count === 0 ? 0.25 : 1,
                      }}
                    />
                  )}
                </div>
                <div style={{ fontSize: '0.625rem', color: colors.textMuted }}>{monthLabel}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTA */}
      <div style={{
        marginTop: '32px',
        padding: '16px 20px',
        backgroundColor: colors.bgAlt,
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
      }}>
        <CTACopy
          isLifetime={isLifetime}
          isWidget={isWidget}
          subscriptionStatus={subscriptionStatus}
          status={status}
          colors={colors}
        />
      </div>

      {/* Footer notes */}
      <div style={{
        marginTop: '20px',
        fontSize: '0.8125rem',
        color: colors.textTertiary,
        lineHeight: 1.5,
      }}>
        Recipients of scheduled emails don't count toward MAU — you can deliver dashboards to unlimited email recipients.
      </div>
      {current?.last_synced_at && (
        <div style={{ marginTop: '6px', fontSize: '0.6875rem', color: colors.textMuted }}>
          Updated {timeAgo(current.last_synced_at)}
          {current.sync_error && (
            <span style={{ color: colors.error, marginLeft: '12px' }}>· last sync errored</span>
          )}
        </div>
      )}
    </div>
  );
};

const CTACopy: React.FC<{
  isLifetime: boolean;
  isWidget: boolean;
  subscriptionStatus: string;
  status: MAUHealthStatus | null;
  colors: ReturnType<typeof pickColors>;
}> = ({ isLifetime, isWidget, subscriptionStatus, status, colors }) => {
  const isTrial = ['trial', 'trial_expired', 'transfer_trial'].includes(subscriptionStatus);

  // Heading by health status
  let heading = 'Need more MAU?';
  if (status === 'persistent')        heading = "Let's talk about right-sizing";
  else if (status === 'critical')     heading = 'Significantly over limit';
  else if (status === 'exceeded')     heading = 'Over your MAU allowance';
  else if (status === 'approaching')  heading = 'Approaching your MAU limit';

  // Path strings differ by context (widget = in-product embed, mgmt = portal)
  const addonPath = isWidget
    ? 'Billing → Subscriptions → Change Plan → Expand the Add-On section'
    : 'Subscription → Change Plan → Expand the Add-Ons section';
  const lifetimePath = isWidget
    ? 'Billing → Subscriptions → Lifetime Add-Ons'
    : 'Subscription → Lifetime Add-Ons';

  // Base CTA by tier type
  let baseCta: string;
  if (isLifetime) {
    baseCta = `+5 MAU lifetime add-on available at ${lifetimePath}`;
  } else if (isTrial) {
    baseCta = 'Upgrade to a paid plan to increase your MAU limit.';
  } else {
    baseCta = `+10 MAU add-on available at ${addonPath}`;
  }

  // Persistent overage wraps the base CTA with extra context
  const action = status === 'persistent'
    ? `You've been over your MAU allowance for two months running. ${baseCta}, or reach out so we can discuss the right plan.`
    : baseCta;

  return (
    <>
      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text, marginBottom: '4px' }}>
        {heading}
      </div>
      <div style={{ fontSize: '0.8125rem', color: colors.textMuted, lineHeight: 1.5 }}>
        {action}
      </div>
    </>
  );
};

export default MAUUsage;
